import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/LanguageContext";
import { PopupProvider } from "@/components/Popup";
import { SelectionProvider } from "@/SelectionContext";
import CookieBanner from "@/components/CookieBanner";
import CouponPopup from "@/components/CouponPopup";
import ScrollToTop from "@/components/ScrollToTop";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import UpdateBanner from "@/components/UpdateBanner";

// Eager-loaded pages (critical path — homepage & direct purchase)
import HomePage from "@/pages/HomePage";
import BuyPixelsPage from "@/pages/BuyPixelsPage";

// Lazy-loaded pages — loaded on-demand when a user navigates to the route.
// Each becomes its own JS chunk, dramatically shrinking the initial bundle.
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const SuccessPage = lazy(() => import("@/pages/SuccessPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const MyBlocksPage = lazy(() => import("@/pages/MyBlocksPage"));
const GoldenTierPage = lazy(() => import("@/pages/GoldenTierPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const AuthorPage = lazy(() => import("@/pages/AuthorPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const AffiliatePage = lazy(() => import("@/pages/AffiliatePage"));
const ChatWidget = lazy(() => import("@/components/ChatWidget"));

// Minimal fallback shown while a lazy route chunk downloads (~100-200ms).
// Keep it ultra-lightweight to avoid its own layout shift.
const RouteFallback = () => (
  <div
    aria-busy="true"
    aria-label="Loading page"
    data-testid="route-fallback"
    style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}
  >
    <div style={{ width: 32, height: 32, border: "3px solid #14B8A6", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
  </div>
);

function App() {
  return (
    <LanguageProvider>
      <PopupProvider>
        <div className="App">
          <BrowserRouter>
            <SelectionProvider>
            <ScrollToTop />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/buy" element={<BuyPixelsPage />} />
                <Route path="/dariousdmt" element={<AdminPage />} />
                <Route path="/success" element={<SuccessPage />} />
                <Route path="/my-blocks" element={<MyBlocksPage />} />
                <Route path="/golden-tier" element={<GoldenTierPage />} />
                <Route path="/blog/author" element={<AuthorPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/affiliate" element={<AffiliatePage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            </SelectionProvider>
          </BrowserRouter>
          <Toaster position="top-right" duration={3000} richColors />
          <Suspense fallback={null}>
            <ChatWidget />
          </Suspense>
          <CookieBanner />
          <CouponPopup />
          <PwaInstallPrompt />
          <UpdateBanner />
        </div>
      </PopupProvider>
    </LanguageProvider>
  );
}

export default App;
